import { create } from '@bufbuild/protobuf'
import { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { EyeOff, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { listConnectedMailboxes } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  AddEmailCaptureExclusionRequestSchema,
  ListConnectedMailboxesRequestSchema,
} from '@/gen/cortado/v1/cortado_pb'
import type { EmailThread } from '@/gen/cortado/v1/emails_pb'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useHasEmailInboxFeature } from '@/hooks/use-has-feature'
import { usePermissions } from '@/hooks/use-permissions'
import { neverCaptureAddedMessage, wipedMessagesDescription } from '@/lib/email-capture-exclusions'
import { useAddEmailCaptureExclusion } from '@/stores/email-capture-exclusions.store'
import { useCurrentUser } from '@/stores/user.store'

import { threadCustomerAddresses } from './email-addresses'

export const WIPE_THREAD_LABEL = 'Also delete the mail already saved in this conversation'
const ADD_FAILED_MESSAGE = "Couldn't add to the Never capture list. Try again."

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ConnectError && err.rawMessage ? err.rawMessage : fallback
}

type NeverCaptureThread = Pick<
  EmailThread,
  'id' | 'participants' | 'connectedMailboxId' | 'connectedMailboxEmail'
>

interface NeverCaptureSenderButtonProps {
  organizationId: string
  thread: NeverCaptureThread
}

/**
 * "Never capture" for a captured conversation (CPRO-783): adds one of the
 * thread's customer addresses to the Never capture list. Owners and admins
 * (Settings Manage) add it for every mailbox; a mailbox's owner adds it for
 * that mailbox only. Hidden for anyone else and for threads not captured by
 * a connected inbox.
 */
export function NeverCaptureSenderButton({ organizationId, thread }: NeverCaptureSenderButtonProps) {
  const { hasFeature } = useHasEmailInboxFeature()
  const { can } = usePermissions(organizationId)
  const { user } = useCurrentUser()
  const canManageAll = can('SettingsManage')
  const captured = !!thread.connectedMailboxId

  // Only a non-admin needs the list, to see whether they own the mailbox.
  const mailboxQuery = useQuery(
    listConnectedMailboxes,
    create(ListConnectedMailboxesRequestSchema, { organizationId }),
    { enabled: hasFeature && captured && !canManageAll && !!organizationId }
  )
  const addMutation = useAddEmailCaptureExclusion()

  const [open, setOpen] = useState(false)
  const [sender, setSender] = useState('')
  const [wipe, setWipe] = useState(false)

  const senders = threadCustomerAddresses(thread.participants)
  const ownsMailbox =
    !!user?.id &&
    (mailboxQuery.data?.mailboxes ?? []).some((m) => m.id === thread.connectedMailboxId && m.userId === user.id)
  const canAdd = canManageAll || ownsMailbox

  if (!hasFeature || !captured || !canAdd || senders.length === 0) return null

  const selectedSender = senders.includes(sender) ? sender : senders[0]
  const scopeCopy = canManageAll
    ? 'Applies to all mailboxes.'
    : `Applies to ${thread.connectedMailboxEmail || 'this mailbox'}.`

  const openDialog = () => {
    setSender(senders[0])
    setWipe(false)
    setOpen(true)
  }

  const submit = async () => {
    try {
      const res = await addMutation.mutateAsync(
        create(AddEmailCaptureExclusionRequestSchema, {
          organizationId,
          mailboxId: canManageAll ? '' : thread.connectedMailboxId,
          pattern: selectedSender,
          wipeThreadId: wipe ? thread.id : '',
        })
      )
      toast.success(neverCaptureAddedMessage(res.exclusion?.pattern || selectedSender.toLowerCase()), {
        description: wipedMessagesDescription(res.wipedMessageCount),
      })
      setOpen(false)
    } catch (err) {
      toast.error(errorMessage(err, ADD_FAILED_MESSAGE))
    }
  }

  const busy = addMutation.isPending

  return (
    <>
      <Button type="button" variant="ghost" size="sm" onClick={openDialog}>
        <EyeOff className="h-3.5 w-3.5" />
        Never capture
      </Button>
      <Dialog open={open} onOpenChange={(next) => !busy && setOpen(next)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Never capture mail from this sender</DialogTitle>
            <DialogDescription>
              Cortado stops saving mail to or from this address. {scopeCopy}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="never-capture-sender">Sender</Label>
              <Select value={selectedSender} onValueChange={setSender} disabled={busy}>
                <SelectTrigger id="never-capture-sender" aria-label="Sender" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {senders.map((address) => (
                    <SelectItem key={address} value={address}>
                      {address}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-start gap-2">
              <Checkbox
                id="never-capture-wipe"
                checked={wipe}
                onCheckedChange={(checked) => setWipe(checked === true)}
                disabled={busy}
              />
              <Label htmlFor="never-capture-wipe">
                <span className="font-normal leading-snug">{WIPE_THREAD_LABEL}</span>
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button disabled={busy} onClick={() => void submit()}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Never capture
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
